import type { Game } from '../../game/Game';
import { setCompanionProvider } from '../../render/ZoneVisuals';
import { registerPanel } from '../../ui/app/App';
import { openPanel } from '../../ui/app/store';
import { PetsPanel } from './PetsPanel';

/**
 * Pets (Lost Ark's companions): a small creature that trots after its owner.
 * The choice rides on the actor's look, so other players see it too.
 */

export interface PetDef {
  id: string;
  name: string;
  description: string;
  model: string;
  scale: number;
  tint?: number;
  /** Character level that unlocks it. */
  level: number;
}

export const PETS: PetDef[] = [
  { id: 'emberpup', name: 'Emberpup', description: 'A hound pup with coals for a heart. Follows anyone who shares their rations.', model: 'mob_hound', scale: 0.42, tint: 0xff9a50, level: 3 },
  { id: 'frostkit', name: 'Frostkit', description: 'A pale wolf cub from the Frosthollow dens, quiet as snowfall.', model: 'mob_wolf', scale: 0.36, tint: 0xe6f2ff, level: 15 },
  { id: 'duneling', name: 'Duneling', description: 'A sand-scorpion hatchling that has decided you are its mother.', model: 'mob_scorpion', scale: 0.26, tint: 0xf0d090, level: 22 },
];

export const petDef = (id: string | undefined): PetDef | undefined => PETS.find((p) => p.id === id);

/** Summons (or dismisses, with null) the local player's pet; the look change replicates. */
export function setPet(game: Game, id: string | null): void {
  const prog = game.progression;
  const p = game.player;
  if (!prog) return;
  const def = id ? petDef(id) : undefined;
  if (id && (!def || prog.char.level < def.level)) return;
  prog.char.flags.pet = id ?? '';
  if (p && game.zone) {
    p.look = { ...p.look, pet: id ?? undefined };
    game.zone.world.emit({ type: 'look', id: p.id, model: p.look.model });
  }
  game.store.saveSoon(game.account);
}

export function install(game: Game): void {
  registerPanel('pets', PetsPanel);
  game.hud.addMenuButton('Pets', '—', () => openPanel('pets'));
  setCompanionProvider((a) => {
    const def = petDef(a.look.pet);
    return def ? { model: def.model, scale: def.scale, tint: def.tint } : null;
  });
  // Your pet comes along into every zone.
  game.zoneSetups.push((g) => {
    const pet = g.progression?.char.flags.pet;
    const p = g.player;
    if (p && typeof pet === 'string' && pet && petDef(pet)) p.look = { ...p.look, pet };
  });
}
